// A new account's first screen (Features 4): "What do you want to use this for?", five big choices and Skip. Each
// card draws the board it makes, in that board's own scene and colours. Never forced: Skip makes today's first
// board, and the answer is kept on the account (WorkspaceDO.init), so it is never asked again on any device.

import { useEffect, useMemo, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { BOARD_KINDS } from "../../shared/boardKinds";
import type { Choice } from "../../shared/onboarding";
import { STARTERS, type StarterSpec } from "../../shared/starters";
import { cx } from "../lib/util";
import { sceneThumb } from "../themes/scenes";
import { resolveTheme, THEME } from "../themes/themes";
import { KindPicture } from "./pictures";
import "./onboarding.css";

/** What each card says it makes, under its line. */
const MAKES: Record<string, string> = { canvas: "Makes a dashboard", plan: "Makes a plan board", calendar: "Makes a content calendar", pipeline: "Makes a client pipeline" };

function Card({ s, busy, onPick }: { s: StarterSpec; busy: Choice | null; onPick: (id: Choice) => void }) {
  const theme = useMemo(() => resolveTheme(s.theme), [s.theme]);
  const def = THEME[s.theme.id];
  const scene = useMemo(() => `url(${sceneThumb(s.theme.id, 320, 168)}) center / cover`, [s.theme.id]);
  return (
    <button className={cx("ob-q-card", busy === s.id && "is-busy")} data-start={s.id} disabled={!!busy} onClick={() => onPick(s.id)}>
      <span className={cx("ob-q-pic", def?.pixel && "is-pixel")} data-mode={def?.mode} style={{ ...(theme.vars as React.CSSProperties), background: scene }}>
        <KindPicture kind={s.kind} />
      </span>
      <span className="ob-q-words">
        <b>
          {s.label}
          {busy === s.id ? <span className="spinner" /> : <ArrowRight size={15} />}
        </b>
        <span>{s.line}</span>
        <small>{MAKES[s.kind] ?? `Makes a ${BOARD_KINDS[s.kind].name.toLowerCase()} board`}</small>
      </span>
    </button>
  );
}

export function StartQuestion({ email, busy, onPick }: { email?: string; busy: Choice | null; onPick: (id: Choice) => void }) {
  const grid = useRef<HTMLDivElement>(null);
  // the first card has the keyboard, and the arrows move between the cards in the order they read
  useEffect(() => {
    grid.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  }, []);
  const onKey = (e: React.KeyboardEvent) => {
    const keys: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(e.key in keys) || !grid.current) return;
    const all = [...grid.current.querySelectorAll<HTMLButtonElement>("button")];
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    e.preventDefault();
    all[(at + keys[e.key] + all.length) % all.length].focus();
  };
  return (
    <>
      <h1 className="ob-q-title">What do you want to use this for?</h1>
      <p className="welcome-sub ob-q-sub">Pick one and your first board is set up for it. You can make any other kind of board later.</p>
      <div className="ob-q-grid" ref={grid} role="group" aria-label="What you want to use it for" onKeyDown={onKey}>
        {STARTERS.map((s) => (
          <Card key={s.id} s={s} busy={busy} onPick={onPick} />
        ))}
      </div>
      <div className="ob-q-foot">
        <button type="button" className="ob-q-skip" data-start="skip" disabled={!!busy} onClick={() => onPick("skip")}>
          {busy === "skip" ? <span className="spinner" /> : "Skip"}
        </button>
        {email && (
          <p className="ob-q-email">
            No board for <b>{email}</b> yet. If yours is under another email, sign in with that one instead.
          </p>
        )}
      </div>
    </>
  );
}
